import React, { useState, useRef, useEffect } from "react";
import { Box, TextField, InputAdornment, ClickAwayListener } from "@mui/material";
import { FaSearch, FaChevronDown } from "react-icons/fa";

const SearchableSelect = ({ 
  label, 
  name, 
  options, 
  value, 
  onChange, 
  placeholder = "Search..." 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValue, setSelectedValue] = useState(value || "");
  const dropdownRef = useRef(null);

  // Filter options based on search
  const filteredOptions = options?.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Find display label for selected value
  const selectedOption = options?.find(opt => opt.value === selectedValue);
  const displayValue = selectedOption?.label || "";

  const handleSelect = (option) => {
    setSelectedValue(option.value);
    setIsOpen(false);
    setSearchTerm("");
    
    // Trigger parent onChange
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: option.value
        }
      });
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={dropdownRef} sx={{ position: "relative", width: "100%" }}>
        {label && (
          <span className="mb-1 text-base font-medium text-gray-700 block">
            {label}<span className="text-[14px] text-[#E72848]">*</span>
          </span>
        )}
        
        {/* Custom Dropdown Trigger */}
        <Box
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            background: "#FFF",
            border: "1px solid #D5D7DA",
            borderRadius: "0.75rem",
            padding: "8px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "40px",
            "&:hover": {
              borderColor: "#E72848",
            },
            "&:focus-within": {
              borderColor: "#E72848",
              boxShadow: "0 0 0 2px rgba(231, 40, 72, 0.2)",
            }
          }}
        >
          <span className={displayValue ? "text-gray-900" : "text-gray-400"}>
            {displayValue || "Select..."}
          </span>
          <FaChevronDown 
            size={14} 
            color="#666" 
            style={{ 
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease"
            }} 
          />
        </Box>

        {/* Dropdown Menu */}
        {isOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "1px solid #D5D7DA",
              borderRadius: "0.75rem",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              zIndex: 1000,
              marginTop: "4px",
              overflow: "hidden"
            }}
          >
            {/* Search Input */}
            <Box sx={{ p: 1, borderBottom: "1px solid #f0f0f0" }}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  background: "#FFF",
                  border: "1px solid #D5D7DA",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  "&:focus-within": {
                    borderColor: "#E72848",
                    boxShadow: "0 0 0 2px rgba(231, 40, 72, 0.2)",
                  }
                }}
              >
                <FaSearch 
                  size={14} 
                  color="#666" 
                  style={{ 
                    marginRight: "8px",
                    flexShrink: 0
                  }} 
                />
                <input 
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={placeholder}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontSize: "14px",
                    background: "transparent",
                    color: "#333",
                    fontFamily: "inherit"
                  }}
                  onKeyDown={(e) => {
                    // Prevent closing dropdown when pressing Enter in search
                    if (e.key === "Enter") {
                      e.stopPropagation();
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Options List */}
            <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Box
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    sx={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f5f5f5",
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                      "&:last-child": {
                        borderBottom: "none",
                      },
                      backgroundColor: selectedValue === option.value ? "#fff0f0" : "transparent",
                      color: selectedValue === option.value ? "#E72848" : "inherit",
                    }}
                  >
                    {option.label}
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    padding: "12px 16px",
                    color: "#666",
                    textAlign: "center",
                    fontStyle: "italic"
                  }}
                >
                  No options found
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default SearchableSelect;